import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import Lenis from "lenis";
import { trackPageView, trackEvent } from "@/analytics";
import { ArrowUpRight, Menu, X, Phone, MapPin, Sparkles, Leaf, ShieldCheck, BedDouble, HeartHandshake, Navigation, Plus, Minus, ChevronLeft, ChevronRight, Check, Star, Hand, Waves, Crown, Gift } from "lucide-react";
import "@/App.css";

const PHONE = "090598 11149";
const PHONE2 = "090598 11129";
const phoneLink2 = "tel:+919059811129";
const LOGO = "https://spincesspa.com/Spinces-Luxury-Spa-Light.png";
const LOGO_ALT = "Spinces Luxury Spa – The Place of Relax, Alwal Hyderabad";
const phoneLink = "tel:+919059811149";
const waLink = (message = "Hello Spinces Luxury Spa, I would like to enquire about booking a massage appointment.") => `https://wa.me/919059811149?text=${encodeURIComponent(message)}`;
const directions = "https://maps.app.goo.gl/wzRqCZKXpQYZNUKr8";
const reviewsUrl = "https://www.google.com/maps?cid=16761468847482420804&g_mp=CiVnb29nbGUubWFwcy5wbGFjZXMudjEuUGxhY2VzLkdldFBsYWNlEAMYASAF&hl=en-US&source=embed";
const mapEmbed = "https://maps.google.com/maps?cid=16761468847482420804&output=embed";
const EASE = [0.22, 1, 0.36, 1];

const IMG = {
  hero: "https://spincesspa.com/hero.jpeg",
  aroma: "https://spincesspa.com/aroma.jpeg",
  balinese: "https://spincesspa.com/balinese.jpeg",
  deep: "https://spincesspa.com/deep.png",
  thai: "https://spincesspa.com/thai.png",
  fourhand: "https://spincesspa.com/fourhand.png",
  body: "https://spincesspa.com/body.jpeg",
  band: "https://spincesspa.com/band.avif",
  interior: "https://spincesspa.com/interior.png",
  
};

const services = [
  { slug: "aroma-therapy-massage", number: "01", name: "Aroma Therapy Massage", duration: "45 / 60 / 90 / 120 min", short: "Warm botanical oils and slow, soothing strokes that quiet the body and settle the mind.", what: "A relaxation massage using warm essential oils and slow, gliding strokes — our gentlest service, perfect for stress relief and better sleep.", who: "Perfect for first-time spa guests, busy professionals under stress, light sleepers, and anyone who prefers gentle, soothing pressure over firm work.", benefits: ["Calms the nervous system and eases stress", "Improves sleep quality", "Lifts mood with natural essential oils", "Relaxes muscles without deep pressure", "Leaves skin soft and nourished"], about: "Aroma Therapy Massage at Spinces combines warm, plant-based essential oils with long, flowing strokes that calm the nervous system. It is our most gentle ritual — the best choice for stress relief, restful sleep and first-time spa guests visiting our Alwal spa near Suchitra, Hyderabad.", image: IMG.aroma, icon: Leaf },
  { slug: "balinese-massage", number: "02", name: "Balinese Massage", duration: "45 / 60 / 90 / 120 min", short: "Flowing strokes, gentle pressure and an unhurried island rhythm for full-body release.", what: "A flowing, medium-pressure massage inspired by Balinese tradition, blending long strokes, skin rolling and gentle acupressure.", who: "Ideal for anyone feeling full-body tiredness, poor circulation or low energy — and for guests who enjoy a flowing, medium-pressure massage.", benefits: ["Boosts blood circulation", "Releases full-body fatigue", "Blends strokes, stretches and acupressure", "Deeply calming island rhythm", "A complete weekly reset for body and mind"], about: "Our Balinese Massage blends gentle stretches, skin rolling and flowing palm pressure inspired by traditional Indonesian techniques. It improves blood circulation and melts away full-body tiredness — a favourite after long work weeks in Hyderabad.", image: IMG.balinese, icon: Sparkles },
  { slug: "deep-tissue-massage", number: "03", name: "Deep Tissue Massage", duration: "45 / 60 / 90 / 120 min", short: "Focused, firm pressure worked deep into muscle tension, knots and everyday fatigue.", what: "A firm, focused massage that reaches the deeper muscle layers to release knots, stiffness and long-held tension.", who: "Made for stiff necks, sore backs, gym-goers and desk workers — anyone carrying knots, tightness or long-held muscle tension.", benefits: ["Releases deep muscle knots and stiffness", "Eases chronic neck, back and shoulder pain", "Speeds recovery after workouts", "Improves posture-related tightness", "Pressure always adjusted to your comfort"], about: "Deep Tissue Massage uses slow, firm pressure to reach the deeper layers of muscle. It is designed for stiff necks, sore backs, gym fatigue and long desk hours. Tell your therapist exactly where it hurts — the entire session is built around it.", image: IMG.deep, icon: ShieldCheck },
  { slug: "thai-massage", number: "04", name: "Thai Massage", duration: "45 / 60 / 90 / 120 min", short: "Rhythmic pressure and assisted stretching that leaves you loose, light and energised.", what: "An oil-free massage with rhythmic pressure and assisted, yoga-style stretches that improve flexibility and energy.", who: "Best for guests who feel stiff or low on energy and want an active, oil-free session — comfortable clothing is all you need.", benefits: ["Improves flexibility and joint mobility", "Energises rather than sedates", "Releases tension through assisted stretches", "Oil-free and fully comfortable", "Leaves you light, loose and awake"], about: "Thai Massage is an energising, oil-free session of rhythmic pressure and assisted yoga-style stretches, enjoyed while comfortably dressed. It improves flexibility, wakes up tired muscles and leaves you feeling lighter and more awake.", image: IMG.thai, icon: HeartHandshake },
  { slug: "four-hand-massage", number: "05", name: "Four Hand Massage", duration: "60 / 90 / 120 min", short: "Two therapists moving in perfect sync — a deeply immersive, twice-as-calming experience.", what: "Two therapists massage you in perfect synchrony — four hands working together for twice the relaxation, starting from 60 minutes.", who: "For those who find it hard to switch off — two therapists working in sync quiet a busy mind twice as fast. Our most luxurious experience.", benefits: ["Twice the relaxation in the same time", "Synchronised strokes quiet a busy mind", "Deeply immersive and unforgettable", "Perfect for special occasions", "Sessions from 60 to 120 minutes"], about: "In our Four Hand Massage, two therapists work in perfect synchrony, so your mind stops tracking the movements and drops into deep rest much faster. It is our most immersive experience, available in 60, 90 and 120 minute sessions.", image: IMG.fourhand, icon: Hand },
  { slug: "body-massage", number: "06", name: "Body Massage", duration: "45 / 60 / 90 / 120 min", short: "A complete head-to-toe massage that eases the whole body into deep, lasting relaxation.", what: "A complete head-to-toe massage covering every major muscle group in one unhurried session.", who: "For anyone who wants everything taken care of in one visit — a complete head-to-toe reset without choosing focus areas.", benefits: ["Covers head to toe in one session", "Releases overall stress and tiredness", "Improves circulation through the whole body", "The easiest first choice at Spinces", "Complimentary scrub included"], about: "The full Body Massage covers head, neck, shoulders, back, arms, legs and feet in one unhurried session. It is the complete reset — the easiest way to relax every muscle in a single visit to Spinces Luxury Spa, Alwal.", image: IMG.body, icon: Waves },
];
const serviceFromSlug = (slug) => services.find((s) => s.slug === slug) || services[0];

const reviews = [
  { name: "Sonu Choudhary", text: "A very good place of relaxation thank q for the management n staff well trained" },
  { name: "Suman Singh", text: "Very good massage I am very happy and full relaxation Divya very good massage I will provide all one very good receiving" },
  { name: "Tilesh", text: "Very good receiving receiving and good massage I am so satisfaction and so much happy strawberry best spa" },
];

const WhatsAppIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
);

const PAGE_META = {
  "/": ["Spinces Luxury Spa in Alwal | Best Luxury Spa & Massage", "Experience relaxation at Spinces Luxury Spa in Alwal, Hyderabad. Enjoy rejuvenating massages and professional spa services near Suchitra. Call or WhatsApp 090598 11149."],
  "/about": ["About Spinces Luxury Spa | Luxury Spa in Alwal, Hyderabad", "Spinces Luxury Spa is a premium luxury spa in Alwal, Hyderabad, near Suchitra — trained therapists, private suites, premium oils and strict hygiene. Discover our story."],
  "/services": ["Massage Services in Alwal | Aroma, Balinese, Deep Tissue, Thai | Spinces Luxury Spa", "Explore 6 professional massage services at Spinces Luxury Spa, Alwal, Hyderabad: Aroma Therapy, Balinese, Deep Tissue, Thai, Four Hand and Body Massage. Sessions from 45–120 minutes."],
  "/membership": ["Spa Membership in Alwal | The Spinces Circle – Spinces Luxury Spa", "Join The Spinces Circle at Spinces Luxury Spa, Alwal — member pricing, priority bookings and exclusive privileges at a luxury spa in Hyderabad. Call or WhatsApp 090598 11149."],
  "/contact": ["Contact Spinces Luxury Spa in Alwal, Hyderabad | Call 090598 11149", "Visit Spinces Luxury Spa, Old Alwal, Hyderabad, near Suchitra. Call or WhatsApp 090598 11149 for spa appointments. Address, map and directions here."],
};

let lenisRef = null;
function SiteRuntime() {
  const location = useLocation();
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenisRef = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); lenisRef = null; };
  }, []);
  useEffect(() => {
    const slug = location.pathname.startsWith("/services/") ? location.pathname.split("/").pop() : null;
    const [title, description] = slug
      ? [`${serviceFromSlug(slug).name} in Alwal, Hyderabad | Spinces Luxury Spa`, `${serviceFromSlug(slug).short} Book at Spinces Luxury Spa, Alwal, Hyderabad. Call or WhatsApp ${PHONE}.`]
      : PAGE_META[location.pathname] || ["Spinces Luxury Spa", "Premium massage spa in Alwal, Hyderabad."];
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", description);
    trackPageView(location);
    if (location.hash) { const t = setTimeout(() => lenisRef && lenisRef.scrollTo(location.hash, { offset: -80 }), 80); return () => clearTimeout(t); }
    if (lenisRef) lenisRef.scrollTo(0, { immediate: true }); else window.scrollTo(0, 0);
  }, [location]);
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest("a");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      const type = href.startsWith("tel:") ? "call_click" : href.includes("wa.me") ? "whatsapp_click" : null;
      if (!type) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: type, cta_location: a.dataset.testid || "unknown", page_path: window.location.pathname });
      trackEvent(type, { cta_location: a.dataset.testid || "unknown", page_path: window.location.pathname });
      try { const key = `spinces-${type}-count`; localStorage.setItem(key, String(Number(localStorage.getItem(key) || 0) + 1)); } catch {}
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);
  return null;
}

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 42 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.95, delay, ease: EASE }}>{children}</motion.div>
);

const NAV_ITEMS = [["/", "Home"], ["/about", "About Us"], ["/services", "Services"], ["/membership", "Membership"], ["/contact", "Contact"]];

function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const close = () => setOpen(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header className={`site-header ${scrolled ? "scrolled" : ""}`} data-testid="site-header">
      <div className="nav-wrap">
        <Link to="/" className="brand" onClick={close} data-testid="brand-home-link"><img src={LOGO} alt={LOGO_ALT} /></Link>
        <button className="mobile-menu" onClick={() => setOpen(!open)} aria-label="Toggle navigation" data-testid="mobile-menu-button">{open ? <X /> : <Menu />}</button>
        <nav className={open ? "nav-links open" : "nav-links"} data-testid="main-navigation">
          {NAV_ITEMS.map(([path, label]) => <Link key={path} className={location.pathname === path ? "active" : ""} to={path} onClick={close} data-testid={`nav-${label.toLowerCase().replace(" ", "-")}-link`}>{label}</Link>)}
          <a href={phoneLink} className="nav-cta" data-testid="header-call-cta"><Phone size={14} /> Call Now</a>
        </nav>
      </div>
    </header>
  );
}

function FloatingActions() {
  return (
    <div className="floating-actions">
      <a href={waLink()} target="_blank" rel="noreferrer" className="float-wa" aria-label="WhatsApp Spinces Luxury Spa" data-testid="floating-whatsapp-button"><WhatsAppIcon size={19} /><span>WhatsApp</span></a>
      <a href={phoneLink} className="float-call" aria-label="Call Spinces Luxury Spa" data-testid="floating-call-button"><Phone size={16} /><span>Call Now</span></a>
    </div>
  );
}

function Footer() {
  return (
    <footer className="footer" data-testid="site-footer">
      <div className="footer-grid">
        <div>
          <Link to="/" className="brand footer-brand" data-testid="footer-brand-link"><img src={LOGO} alt={LOGO_ALT} /></Link>
          <p className="footer-lead">Spinces Luxury Spa - Alwal Hyderabad</p>
          <div className="footer-social">
            <a href={waLink()} target="_blank" rel="noreferrer" aria-label="WhatsApp Spinces Luxury Spa" data-testid="footer-whatsapp-link"><WhatsAppIcon size={17} /></a>
            <a href={phoneLink} aria-label="Call Spinces Luxury Spa" data-testid="footer-phone-link"><Phone size={17} /></a>
            <a href={directions} target="_blank" rel="noreferrer" aria-label="Get directions to Spinces Luxury Spa" data-testid="footer-directions-link"><Navigation size={17} /></a>
          </div>
        </div>
        <div><h4>Explore</h4><Link to="/about" data-testid="footer-about-link">About Us</Link><Link to="/services" data-testid="footer-services-link">Our Services</Link><Link to="/membership" data-testid="footer-membership-link">Membership</Link><Link to="/contact" data-testid="footer-contact-link">Contact Us</Link></div>
        <div><h4>Our services</h4>{services.map((s) => <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`footer-${s.slug}-link`}>{s.name}</Link>)}</div>
        <div className="footer-contact"><h4>Visit us</h4><a href={phoneLink} data-testid="footer-phone-text">{PHONE}</a><a href={phoneLink2} data-testid="footer-phone2-text">{PHONE2}</a><p>4th Floor, 1055/87/1, Road No. 1,<br />Above Polar Bear, Old Alwal,<br />Hyderabad, Telangana 500010</p><a href={directions} target="_blank" rel="noreferrer" className="text-link" data-testid="footer-map-link">Get directions <ArrowUpRight size={14} /></a></div>
      </div>
      <div className="footer-bottom"><span>© 2026 Spinces Luxury Spa. All Rights Reserved.</span><span>Professional · Peaceful · Hygienic · Relaxing</span><span>Design &amp; Developed by <a href="https://hubwalt.com/" target="_blank" rel="noreferrer" className="footer-credit" data-testid="footer-hubwalt-link">Hubwalt</a></span></div>
    </footer>
  );
}

function Layout({ children }) { return <><Header />{children}<FloatingActions /><Footer /></>; }

function Eyebrow({ children }) { return <span className="eyebrow"><span className="eyebrow-line" />{children}</span>; }

function SectionIntro({ eyebrow, title, text, center = false }) {
  return (
    <Reveal className={`section-intro ${center ? "center" : ""}`}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2>{title}</h2>
      {text && <p>{text}</p>}
    </Reveal>
  );
}

function Marquee() {
  const items = ["Renew", "Restore", "Aroma Therapy", "Balinese", "Deep Tissue", "Thai Massage", "Four Hand", "Body Massage", "Alwal · Hyderabad"];
  return (
    <div className="marquee" id="highlights" aria-hidden="true" data-testid="editorial-marquee">
      <div className="marquee-track">{[...items, ...items].map((t, i) => <span className="marquee-item" key={i}>{t}<i /></span>)}</div>
    </div>
  );
}

function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, 220]);
  const scale = useTransform(scrollY, [0, 900], [1.06, 1.2]);
  const videoRef = useRef(null);
  useEffect(() => { const v = videoRef.current; if (v) v.play().catch(() => {}); }, []);
  const lines = [<>The Art of</>, <>Deep <i>Rest.</i></>];
  return (
    <section className="hero" id="hero" data-testid="home-hero">
      <motion.div className="hero-media" style={{ y, scale }}>
        <video ref={videoRef} className="hero-video" autoPlay muted loop playsInline preload="auto" poster={IMG.hero} aria-hidden="true">
          <source src="https://assets.mixkit.co/videos/14781/14781-720.mp4" type="video/mp4" />
        </video>
      </motion.div>
      <div className="hero-shade" />
      <div className="hero-inner">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.8, ease: EASE }}><Eyebrow>Luxury massage spa in Alwal, Hyderabad</Eyebrow></motion.div>
        <h1>{lines.map((line, i) => <span className="mask" key={i}><motion.span initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1.15, delay: 0.4 + i * 0.16, ease: EASE }}>{line}</motion.span></span>)}</h1>
        <motion.p className="hero-sub" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.9, ease: EASE }}>Six premium massage services in a warm, dark, quiet room — with trained therapists, premium oils and sessions of 45 / 60 / 90 / 120 minutes, paced entirely around you.</motion.p>
        <motion.div className="hero-actions" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.9, ease: EASE }}>
          <a href={waLink()} target="_blank" rel="noreferrer" className="button button-gold" data-testid="hero-whatsapp-button"><WhatsAppIcon size={17} /> Book on WhatsApp</a>
          <a href={phoneLink} className="button button-outline" data-testid="hero-book-button"><Phone size={15} /> Call to book</a>
        </motion.div>
        <motion.div className="hero-trust-row" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.35, duration: 0.9 }} data-testid="hero-trust-row">
          <span className="stars">{[...Array(5)].map((_, i) => <Star key={i} size={13} fill="currentColor" />)}</span> Rated 4.6 on Google <i /> 30 reviews <i /> Old Alwal, Hyderabad
        </motion.div>
      </div>
      <motion.div className="hero-badges" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 1 }}>Professional <span /> Peaceful <span /> Hygienic <span /> Relaxing</motion.div>
      <div className="scroll-cue" aria-hidden="true"><span /></div>
    </section>
  );
}

function Intro() {
  return (
    <section className="intro section-pad" id="about_us">
      <Reveal className="intro-media">
        <div className="frame"><img src={IMG.interior} alt="Candle-lit oils and fresh towels inside Spinces Luxury Spa, Alwal — the best spa in Alwal" /></div>
        <div className="frame-caption"><span>Spinces</span><small>Alwal · Hyderabad</small></div>
      </Reveal>
      <div className="intro-copy">
        <SectionIntro eyebrow="Welcome to Spinces" title={<>A moment of calm,<br /><i>made for you.</i></>} text="Spinces Luxury Spa is a premium massage spa in Alwal, Hyderabad — a calm, candle-warm space where you can step away from daily stress and enjoy dedicated time for your body and mind." />
        <Reveal delay={0.15}><p className="intro-extra">Choose from six easy-to-understand services: Aroma Therapy, Balinese, Deep Tissue, Thai, Four Hand and full Body Massage. Sessions run 45 to 120 minutes, and every one is paced around your comfort and preferred pressure.</p><Link to="/services" className="text-link" data-testid="intro-services-link">Explore our services <ArrowUpRight size={15} /></Link></Reveal>
        <Reveal delay={0.25} className="stats">
          <div><strong>06</strong><span>Massage services</span></div>
          <div><strong>45–120</strong><span>Minute sessions</span></div>
          <div><strong>4.6</strong><span>Google rating</span></div>
        </Reveal>
      </div>
    </section>
  );
}

function BentoCard({ service, delay = 0 }) {
  const Icon = service.icon;
  return (
    <Reveal className={`bento-card ${service.span}`} delay={delay}>
      <Link to={`/services/${service.slug}`} className="bento-link" data-testid={`service-${service.slug}-card`}>
        <img src={service.image} alt={`${service.name} at Spinces Luxury Spa, Alwal Hyderabad`} loading="lazy" />
        <span className="bento-shade" />
        <span className="bento-number">{service.number}</span>
        <span className="bento-arrow"><ArrowUpRight size={18} /></span>
        <div className="bento-content"><Icon size={19} strokeWidth={1.4} /><h3>{service.name}</h3><p>{service.short}</p></div>
      </Link>
    </Reveal>
  );
}

function ServicesBento() {
  const order = [services[4], services[0], services[1], services[5], services[2], services[3]];
  const spans = ["s7", "s5", "s5", "s7", "s6", "s6"];
  return (
    <section className="services-preview section-pad" id="services" data-testid="services-bento">
      <SectionIntro eyebrow="The menu" title={<>Six services. <i>One quiet hour.</i></>} text="Simple, thoughtfully paced massages for rest, release and recovery." />
      <div className="bento">{order.map((s, i) => <BentoCard service={{ ...s, span: spans[i] }} key={s.slug} delay={i * 0.08} />)}</div>
      <Reveal className="center-link" delay={0.2}><Link to="/services" className="button button-light" data-testid="view-all-services-button">Explore all services <ArrowUpRight size={16} /></Link></Reveal>
    </section>
  );
}

function WhyUs() {
  const features = [
    [Sparkles, "Skilled therapists", "Trained, experienced therapists who work at an unhurried pace."],
    [Leaf, "Premium natural oils", "Warm, skin-friendly oil blends chosen for each service."],
    [ShieldCheck, "Hygiene first", "Fresh linen, sanitised rooms and spotless presentation."],
    [BedDouble, "Private suites", "Dim, warm, quiet rooms made for deep switching-off."],
    [HeartHandshake, "Tailored pressure", "Your preferred depth and focus areas, every single visit."],
    [MapPin, "Easy to reach", "A convenient location in Old Alwal, Hyderabad."],
  ];
  return (
    <section className="why section-pad" id="spa" data-testid="why-choose-us">
      <div className="why-head">
        <SectionIntro eyebrow="Why choose us" title={<>Why guests choose <i>Spinces.</i></>} text="Every detail is considered so your time with us feels effortless." />
        <Reveal delay={0.2} className="why-ornament"><img src="/lotus.svg" alt="Spinces Luxury Spa lotus mark" /></Reveal>
      </div>
      <div className="feature-grid">{features.map(([Icon, title, text], i) => <Reveal className="feature" key={title} delay={i * 0.06}><Icon size={24} strokeWidth={1.2} /><h3>{title}</h3><p>{text}</p></Reveal>)}</div>
    </section>
  );
}

function ParallaxBand() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-14%", "14%"]);
  return (
    <section className="band" id="experience" ref={ref} data-testid="experience-band">
      <motion.img src={IMG.band} alt="Warm oil massage treatment at Spinces Luxury Spa" style={{ y }} />
      <div className="band-shade" />
      <div className="band-content">
        <Reveal><Eyebrow>The Spinces feeling</Eyebrow></Reveal>
        <Reveal delay={0.1}><h2>Somewhere between<br /><i>here and nowhere.</i></h2></Reveal>
        <Reveal delay={0.2}><p>A warm room, low light, warm oils and slow hands. Your time to relax begins the moment you decide to make time for yourself.</p></Reveal>
        <Reveal delay={0.3}><a href={waLink()} target="_blank" rel="noreferrer" className="text-link light" data-testid="band-whatsapp-link">Find your calm <ArrowUpRight size={15} /></a></Reveal>
      </div>
    </section>
  );
}

function Steps() {
  const steps = [["01", "Choose your service", "Pick the massage that suits how you want to feel."], ["02", "Call or WhatsApp", "Reach us directly — no forms, no waiting."], ["03", "Confirm your time", "Share your preferred date, time and duration."], ["04", "Arrive & relax", "Settle into the quiet and enjoy your session."]];
  return (
    <section className="steps section-pad" id="how-it-works">
      <SectionIntro eyebrow="The journey" title={<>Your time, <i>well spent.</i></>} />
      <div className="steps-grid">{steps.map(([n, t, d], i) => <Reveal className="step" key={n} delay={i * 0.08}><span>{n}</span><h3>{t}</h3><p>{d}</p></Reveal>)}</div>
    </section>
  );
}

function FaqList({ faqs, testidPrefix, firstOpen = false }) {
  return <div className="faq-list">{faqs.map(([q, a], i) => <Reveal key={q} delay={i * 0.05}><details open={firstOpen && i === 0}><summary data-testid={`${testidPrefix}-${q.slice(0, 10).replaceAll(" ", "-")}`}><span className="faq-q"><span className="faq-num">{String(i + 1).padStart(2, "0")}</span>{q}</span><span className="faq-toggle"><Plus className="icon-plus" size={15} /><Minus className="icon-minus" size={15} /></span></summary><div className="faq-a"><div><p>{a}</p></div></div></details></Reveal>)}</div>;
}

function HomeFaq() {
  const faqs = [
    ["Which massage is best for first-time guests?", "Aroma Therapy or a full Body Massage are wonderful introductions. Both can be adapted from gentle to medium pressure, and our team will ask how you are feeling and recommend the best service before your session."],
    ["How early should I arrive for my appointment?", "Arriving 10 minutes early is perfect — enough time to settle in, share your pressure preferences and start your session completely unhurried."],
    ["What should I wear during a massage?", "Come in anything comfortable. We provide fresh linens and everything you need, and you are always comfortably draped throughout the session."],
    ["Can the massage pressure be customised?", "Always. Choose gentle, medium or firm, and tell your therapist at any point — every session is adjusted to your comfort."],
    ["When should I avoid or postpone a massage?", "Please postpone if you have a fever, a recent injury or surgery, or a skin infection. If you are unsure, check with your doctor first — then reschedule with us anytime on WhatsApp."],
    ["How can I book or reschedule an appointment?", `Just call or WhatsApp us at ${PHONE}. Bookings and reschedules are confirmed within minutes — no forms needed.`],
  ];
  return (
    <section className="faq section-pad" id="faqs" data-testid="home-faq">
      <Reveal className="faq-side">
        <Eyebrow>Before your visit</Eyebrow>
        <h2>Your questions,<br /><i>answered</i></h2>
        <p>Everything you may want to know before relaxing into your Spinces experience.</p>
        <div className="faq-card">
          <WhatsAppIcon size={22} />
          <strong>Still have a question?</strong>
          <span>Our spa team is happy to help.</span>
          <a href={waLink("Hello Spinces Luxury Spa, I have a question before booking.")} target="_blank" rel="noreferrer" className="faq-card-link" data-testid="faq-whatsapp-link">Ask on WhatsApp <ArrowUpRight size={14} /></a>
        </div>
      </Reveal>
      <FaqList faqs={faqs} testidPrefix="home-faq" firstOpen />
    </section>
  );
}

function Reviews() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selected, setSelected] = useState(0);
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    const timer = setInterval(() => emblaApi.scrollNext(), 5200);
    return () => { clearInterval(timer); emblaApi.off("select", onSelect); };
  }, [emblaApi]);
  return (
    <section className="testimonial section-pad" id="reviews" data-testid="reviews-section">
      <div className="testimonial-heading">
        <Reveal><Eyebrow>Guest experiences</Eyebrow>
        <h2>Loved by guests,<br /><i>rated on Google.</i></h2>
        <div className="rating-badge" data-testid="google-rating-badge"><strong>4.6</strong><div><div className="stars">{[...Array(5)].map((_, i) => <Star key={i} size={15} fill="currentColor" />)}</div><span>Google rating</span></div></div>
        <a href={reviewsUrl} target="_blank" rel="noreferrer" className="text-link" data-testid="google-reviews-link">Read all reviews on Google <ArrowUpRight size={14} /></a></Reveal>
      </div>
      <Reveal className="reviews-slider" delay={0.15}>
        <div className="reviews-viewport" ref={emblaRef} data-testid="reviews-viewport">
          <div className="reviews-track">
            {reviews.map((r) => (
              <figure className="review-slide" key={r.name} data-testid={`review-${r.name.toLowerCase().replaceAll(" ", "-")}`}>
                <div className="stars">{[...Array(5)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}</div>
                <blockquote>“{r.text}”</blockquote>
                <figcaption><strong>{r.name}</strong><span>Google review</span></figcaption>
              </figure>
            ))}
          </div>
        </div>
        <div className="reviews-nav">
          <button onClick={() => emblaApi && emblaApi.scrollPrev()} aria-label="Previous review" data-testid="review-prev-button"><ChevronLeft size={18} /></button>
          <div className="reviews-dots">{reviews.map((_, i) => <button key={i} className={i === selected ? "active" : ""} onClick={() => emblaApi && emblaApi.scrollTo(i)} aria-label={`Go to review ${i + 1}`} data-testid={`review-dot-${i}`} />)}</div>
          <button onClick={() => emblaApi && emblaApi.scrollNext()} aria-label="Next review" data-testid="review-next-button"><ChevronRight size={18} /></button>
        </div>
      </Reveal>
    </section>
  );
}

function CtaBanner() {
  return (
    <section className="cta-band" id="prices" data-testid="cta-banner">
      <img className="cta-bg" src={IMG.body} alt="" aria-hidden="true" />
      <span className="cta-shade" />
      <Reveal className="cta-inner">
        <Eyebrow>Make time for yourself</Eyebrow>
        <h2>Ready to <i>relax?</i></h2>
        <p>One call is all it takes. Tell us the service and time you prefer — we'll take care of the rest.</p>
        <a href={phoneLink} className="cta-phone" data-testid="cta-big-phone"><Phone size={30} strokeWidth={1.4} /> {PHONE}</a>
        <div className="cta-actions">
          <a href={phoneLink} className="button button-gold" data-testid="direct-call-cta"><Phone size={16} /> Call now</a>
          <a href={waLink()} target="_blank" rel="noreferrer" className="button button-outline" data-testid="direct-whatsapp-cta"><WhatsAppIcon size={17} /> WhatsApp us</a>
        </div>
      </Reveal>
    </section>
  );
}

function PageHero({ eyebrow, title, image }) {
  return (
    <section className="page-hero" data-testid="page-hero">
      <img src={image} alt="" className="page-hero-bg" />
      <div className="page-hero-shade" />
      <div className="page-hero-inner">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7, ease: EASE }}><Eyebrow>{eyebrow}</Eyebrow></motion.div>
        <h1><span className="mask"><motion.span initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1.05, delay: 0.35, ease: EASE }}>{title}</motion.span></span></h1>
      </div>
    </section>
  );
}

function Appointment() {
  return (
    <section className="appt section-pad" id="contact" data-testid="appointment-section">
      <div className="appt-card">
        <Reveal className="appt-left">
          <Eyebrow>Book on call or WhatsApp</Eyebrow>
          <h2>Make an <i>appointment</i></h2>
          <p>Tell us your service and preferred time. We confirm your appointment in minutes.</p>
          <div className="appt-address"><MapPin size={20} /><p>4th Floor, 1055/87/1, Road No. 1, Above Polar Bear, HMT Officers Colony, Old Alwal, Hyderabad, Telangana 500010</p></div>
          <a href={directions} target="_blank" rel="noreferrer" className="button button-outline" data-testid="appt-directions-button"><Navigation size={15} /> Get directions</a>
        </Reveal>
        <Reveal className="appt-right" delay={0.12}>
          <a href={phoneLink} className="appt-tile" data-testid="appt-call-card">
            <span className="appt-ico call"><Phone size={22} /></span>
            <span className="appt-tile-body"><small>Call to book</small><strong>{PHONE}</strong><em>Tap to call the spa directly</em></span>
            <ArrowUpRight size={18} className="appt-arrow" />
          </a>
          <a href={waLink()} target="_blank" rel="noreferrer" className="appt-tile" data-testid="appt-whatsapp-card">
            <span className="appt-ico wa"><WhatsAppIcon size={22} /></span>
            <span className="appt-tile-body"><small>WhatsApp booking</small><strong>Chat with us</strong><em>Send your service and time, we confirm on chat</em></span>
            <ArrowUpRight size={18} className="appt-arrow" />
          </a>
          <p className="appt-chips-label">Or tap a service to start a WhatsApp booking:</p>
          <div className="appt-chips">
            {services.map((s) => <a key={s.slug} href={waLink(`Hello Spinces Luxury Spa, I would like to book a ${s.name}.`)} target="_blank" rel="noreferrer" className="appt-chip" data-testid={`appt-chip-${s.slug}`}><WhatsAppIcon size={13} />{s.name}</a>)}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Home() {
  return <Layout><main><Hero /><Marquee /><Intro /><ServicesBento /><WhyUs /><ParallaxBand /><Steps /><Appointment /><HomeFaq /><Reviews /><CtaBanner /></main></Layout>;
}

function About() {
  const values = [["01", "Comfort", "Thoughtful spaces and a gentle pace from arrival to goodbye."], ["02", "Cleanliness", "A well-presented, hygienic environment you can settle into."], ["03", "Professional service", "A warm, considered approach centred on your comfort."], ["04", "Guest experience", "Time and attention dedicated to making your visit feel special."]];
  return (
    <Layout><main>
      <PageHero eyebrow="Our story" title={<>Spinces Luxury Spa <i>Alwal · Hyderabad</i></>} image={IMG.interior} />
      <section className="about-story section-pad">
        <div>
          <SectionIntro eyebrow="A softer pace" title={<>Relaxation should feel <i>effortless.</i></>} />
          <Reveal delay={0.1}><p>Spinces Luxury Spa is a premium massage spa in Alwal, Hyderabad, created for guests who want to relax, recharge and enjoy professional massage services in a comfortable, dark and quiet environment.</p><p>Our approach is simple: a calm atmosphere, skilled therapists, premium oils, strict hygiene and six carefully designed massage services that are easy to understand and choose from.</p></Reveal>
          <Reveal delay={0.2} className="stats"><div><strong>06</strong><span>Massage services</span></div><div><strong>45–120</strong><span>Minute sessions</span></div><div><strong>4.6</strong><span>Google rating</span></div></Reveal>
        </div>
        <Reveal className="intro-media" delay={0.15}><div className="frame"><img src={IMG.hero} alt="Therapist performing a luxury massage at Spinces Luxury Spa" /></div></Reveal>
      </section>
      <WhyUs />
      <section className="commitment section-pad">
        <SectionIntro eyebrow="Our commitment" title={<>The little details make <i>the difference.</i></>} text="From the moment you enter, every detail contributes to a peaceful experience — the ambience, the treatment environment and the professional approach of our team." />
        <div className="commitment-grid">{values.map(([n, t, d], i) => <Reveal key={n} delay={i * 0.07}><span>{n}</span><h3>{t}</h3><p>{d}</p></Reveal>)}</div>
      </section>
      <CtaBanner />
    </main></Layout>
  );
}

function DirectoryCard({ service, delay }) {
  const Icon = service.icon;
  return (
    <Reveal delay={delay} className="dir-card-wrap">
      <article className="dir-card">
        <Link to={`/services/${service.slug}`} className="dir-img" data-testid={`service-${service.slug}-image-link`}><img src={service.image} alt={`${service.name} at Spinces Luxury Spa, Alwal`} loading="lazy" /><span className="bento-number">{service.number}</span><span className="bento-arrow"><ArrowUpRight size={18} /></span></Link>
        <div className="dir-content"><Icon size={20} strokeWidth={1.4} /><h3>{service.name}</h3><p>{service.short}</p><div className="dir-meta"><span className="dir-duration">{service.duration}</span><Link to={`/services/${service.slug}`} className="dir-link" data-testid={`service-${service.slug}-details-link`}>View details <ArrowUpRight size={13} /></Link></div></div>
      </article>
    </Reveal>
  );
}

function Services() {
  return (
    <Layout><main>
      <PageHero eyebrow="The menu" title={<>Our massage <i>services.</i></>} image={IMG.body} />
      <section className="directory section-pad">
        <SectionIntro eyebrow="Find your service" title={<>Made for how you want <i>to feel.</i></>} text="Sessions come in 45 / 60 / 90 / 120 minutes. Four Hand Massage starts from 60 minutes." />
        <div className="directory-grid">{services.map((s, i) => <DirectoryCard service={s} key={s.slug} delay={(i % 2) * 0.1} />)}</div>
      </section>
      <WhyUs />
      <Marquee />
      <CtaBanner />
    </main></Layout>
  );
}

function ServiceDetail() {
  const { slug } = useParams();
  const service = serviceFromSlug(slug);
  const others = services.filter((s) => s.slug !== service.slug).slice(0, 3);
  const nameParts = service.name.split(" ");
  const expect = ["A calm welcome and a warm, private suite", "Premium oils and techniques at your preferred pressure", "Time to settle in — never rushed, never hurried", "A slow, peaceful close to your visit"];
  const faqs = [[`What is ${service.name}?`, service.what], ["How long is a session?", `${service.name} sessions run ${service.duration.replace("–", "to")}. Choose the length that suits your day.`], ["How can I book?", `Call or WhatsApp us at ${PHONE} with your preferred date and time. We confirm quickly.`], ["Do I need an appointment?", "Appointments are recommended so your room and therapist are ready. Walk-ins are welcome when slots are free."]];
  return (
    <Layout><main>
      <PageHero eyebrow={`Service ${service.number}`} title={<>{nameParts.slice(0, -1).join(" ")} <i>{nameParts.slice(-1)}</i></>} image={service.image} />
      <section className="detail section-pad">
        <div className="detail-main">
          <Reveal><Eyebrow>A premium experience</Eyebrow>
          <h2>Slow down, <i>beautifully.</i></h2>
          <p>{service.about} Every session takes place in a dark, warm, private suite — never rushed, always at your preferred pressure.</p>
          <div className="detail-meta"><span>Session length</span><strong>{service.duration}</strong></div>
          <div className="detail-actions">
            <a href={phoneLink} className="button button-gold" data-testid="detail-book-button"><Phone size={15} /> Call to book</a>
            <a href={waLink(`Hello Spinces Luxury Spa, I would like to enquire about the ${service.name}.`)} target="_blank" rel="noreferrer" className="button button-outline" data-testid="detail-whatsapp-button"><WhatsAppIcon size={17} /> WhatsApp enquiry</a>
          </div></Reveal>
        </div>
        <Reveal className="detail-aside" delay={0.15}><h3>What to expect</h3><div className="aside-perk" data-testid="complimentary-scrub"><Gift size={18} /><div><strong>Complimentary scrub included</strong><span>A refreshing body scrub comes free with every massage session</span></div></div>{expect.map((t) => <div key={t}><Check size={16} />{t}</div>)}</Reveal>
      </section>
      <section className="service-info section-pad" data-testid="service-info">
        <Reveal className="info-block"><Eyebrow>What is it</Eyebrow><h2>What is {service.name}?</h2><p>{service.what} {service.about}</p></Reveal>
        <div className="info-cols">
          <Reveal className="info-col"><h3>Who it is for</h3><p>{service.who}</p></Reveal>
          <Reveal className="info-col" delay={0.12}><h3>Benefits of {service.name}</h3><ul>{service.benefits.map((b) => <li key={b}><Check size={15} />{b}</li>)}</ul></Reveal>
        </div>
      </section>
      <section className="more-services section-pad">
        <SectionIntro eyebrow="Keep exploring" title={<>Other <i>services.</i></>} />
        <div className="bento bento-small">{others.map((s, i) => <BentoCard service={{ ...s, span: "s4" }} key={s.slug} delay={i * 0.08} />)}</div>
      </section>
      <section className="faq section-pad">
        <SectionIntro eyebrow="Good to know" title={<>Questions, <i>answered.</i></>} />
        <FaqList faqs={faqs} testidPrefix="faq" />
      </section>
      <CtaBanner />
    </main></Layout>
  );
}

function Membership() {
  const tiers = [
    { name: "SILVER", tag: "For occasional resets", featured: false, perks: ["Members-only pricing on all six services", "Priority weekday appointments", "Flexible rescheduling", "A little birthday luxury on us"] },
    { name: "GOLD", tag: "For regular rest", featured: true, perks: ["Everything in Silver", "Your preferred therapist, reserved for you", "Priority weekend and evening slots", "A guest pass to share the calm", "Members-only seasonal experiences"] },
    { name: "PLATINUM", tag: "For the truly devoted", featured: false, perks: ["Everything in Gold", "First access to new services and suites", "A dedicated booking concierge on WhatsApp", "Festive gifts and quiet surprises"] },
  ];
  const steps = [["01", "Call or WhatsApp", `Message us at ${PHONE} and mention the Spinces Circle.`], ["02", "Choose your plan", "We'll walk you through current plans and help you pick the right tier."], ["03", "Book with priority", "Enjoy member pricing, preferred slots and small luxuries all year."]];
  return (
    <Layout><main>
      <PageHero eyebrow="Membership" title={<>The Spinces <i>Circle.</i></>} image={IMG.body} />
      <section className="membership-intro section-pad">
        <SectionIntro center eyebrow="Make rest a habit" title={<>Your calm, <i>on membership.</i></>} text="The Spinces Circle turns an occasional visit into a ritual you can count on — member pricing, priority slots and quiet privileges reserved for our regulars. Call or WhatsApp us for current plans and pricing." />
        <Reveal className="center-link" delay={0.1}><a href={waLink("Hello Spinces Luxury Spa, I would like to know about Spinces Circle membership plans.")} target="_blank" rel="noreferrer" className="button button-gold" data-testid="membership-hero-cta"><WhatsAppIcon size={17} /> Ask about membership</a></Reveal>
      </section>
      <section className="tiers section-pad" data-testid="membership-tiers">
        {tiers.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.1} className={`tier-wrap ${t.featured ? "featured" : ""}`}>
            <div className={`tier ${t.featured ? "featured" : ""}`}>
              {t.featured && <span className="tier-flag"><Crown size={11} /> Most loved</span>}
              <h3>{t.name}</h3>
              <p className="tier-tag">{t.tag}</p>
              <ul>{t.perks.map((p) => <li key={p}><Check size={15} />{p}</li>)}</ul>
              <a href={waLink(`Hello Spinces Luxury Spa, I would like to know about the ${t.name} membership.`)} target="_blank" rel="noreferrer" className={`button ${t.featured ? "button-gold" : "button-outline"}`} data-testid={`membership-${t.name.toLowerCase().replaceAll(" ", "-")}-cta`}>Enquire on WhatsApp</a>
            </div>
          </Reveal>
        ))}
      </section>
      <section className="steps section-pad">
        <SectionIntro eyebrow="How it works" title={<>Joining is <i>simple.</i></>} />
        <div className="steps-grid three">{steps.map(([n, t, d], i) => <Reveal className="step" key={n} delay={i * 0.08}><span>{n}</span><h3>{t}</h3><p>{d}</p></Reveal>)}</div>
      </section>
      <CtaBanner />
    </main></Layout>
  );
}

function Contact() {
  return (
    <Layout><main>
      <PageHero eyebrow="Come and unwind" title={<>Spinces Luxury Spa <i>Alwal · Hyderabad</i></>} image={IMG.hero} />
      <section className="contact-details section-pad">
        <div>
          <SectionIntro eyebrow="Find us in Alwal" title={<>Make space for <i>yourself.</i></>} text="We're here to help you find the right service and a time that feels right." />
          <Reveal delay={0.1} className="address"><MapPin size={22} /><div><h3>Our address</h3><p>4th Floor, 1055/87/1, Road No. 1,<br />Above Polar Bear, HMT Officers Colony,<br />Old Alwal, Hyderabad, Telangana 500010</p><a href={directions} target="_blank" rel="noreferrer" className="text-link" data-testid="contact-directions-link">Get directions <ArrowUpRight size={15} /></a></div></Reveal>
          <Reveal delay={0.2} className="contact-direct">
            <a href={phoneLink} data-testid="contact-call-link"><Phone size={19} /><span><small>Call us</small>{PHONE}</span></a>
            <a href={phoneLink2} data-testid="contact-call2-link"><Phone size={19} /><span><small>Or call</small>{PHONE2}</span></a>
            <a href={waLink()} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link"><WhatsAppIcon size={19} /><span><small>Message anytime</small>WhatsApp us</span></a>
          </Reveal>
        </div>
        <Reveal className="intro-media" delay={0.15}>
          <div className="frame"><img src={IMG.interior} alt="Inside Spinces Luxury Spa, Alwal — candles, oils and fresh towels" /></div>
          <div className="frame-caption"><span>Spinces</span><small>Luxury Spa · Alwal</small></div>
        </Reveal>
      </section>
      <section className="map-section" data-testid="contact-map">
        <iframe title="Spinces Luxury Spa location on Google Maps" src={mapEmbed} loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" data-testid="google-map-embed" />
      </section>
      <CtaBanner />
    </main></Layout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <SiteRuntime />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/services/:slug" element={<ServiceDetail />} />
        <Route path="/membership" element={<Membership />} />
        <Route path="/contact" element={<Contact />} />
      </Routes>
    </BrowserRouter>
  );
}
export default App;
